// returns true if the cart product has a product
const hasProduct = cartProduct => {
  return cartProduct && cartProduct.product
}

// Get the account discount amount for a cart product
// Checks the cartProduct first, then falls back to financial aid approvals
const getAccountDiscountCents = (cartProduct, account = null) => {
  let accountDiscountCents = cartProduct.accountDiscount?.discountCents || 0

  // If no accountDiscount exists, check for active financial aid approval
  // Apply FA if available, but NOT to virtual cart products (existing plans with days=0)
  if (!accountDiscountCents && account && cartProduct.session) {
    // Don't apply FA retroactively to existing plans (virtual cart products with days=0)
    if (cartProduct.plan && (!cartProduct.days || cartProduct.days === 0)) {
      return 0
    }

    // Only apply financial aid if it's available for this session
    if (!cartProduct.session.financialAidAvailable) {
      return 0
    }

    const sessionYear = cartProduct.session.year
    const activeApproval = account.financialAidApprovals?.find(
      approval => approval.year === sessionYear && approval.status === 'active'
    )
    if (activeApproval) {
      accountDiscountCents = activeApproval.discountCents
    }
  }

  return accountDiscountCents
}

/**
returns true if the cart product has any days (retail or guest pass)
@returns {boolean}
**/
const hasDays = cartProduct => {
  if (!hasProduct(cartProduct)) return false

  return cartProduct.days > 0 || cartProductTotal(cartProduct) > 0
}

// Total price for the specified cart product
// NOTE: ensure this logic stays consistent with lib/decorators/cart_product.rb
const cartProductTotal = (cartProduct, account = null) => {
  if (!hasProduct(cartProduct)) return 0

  const dayPriceCents = dayPrice(cartProduct, cartProduct.days, account)

  // If adding days to an existing plan with the same product AND same day price AND no balance due,
  // just charge for new days. But if the day price changes (due to volume discount, FA, etc.) or there's
  // a balance due (receivable or bad debt), treat it as an upgrade with credits to properly account for it.
  const hasBalanceDue =
    cartProduct.plan &&
    ((cartProduct.plan.financials?.receivable || 0) > 0 ||
      (cartProduct.plan.financials?.bad_debt || 0) > 0)

  if (cartProduct.days === 0 && hasBalanceDue) {
    // Paying balance due without adding days - charge receivable + bad_debt
    const receivableCents = cartProduct.plan.financials?.receivable || 0
    const badDebtCents = cartProduct.plan.financials?.bad_debt || 0
    return receivableCents + badDebtCents
  }

  // Check if volume discount is newly triggered by adding these days
  const oldDays = cartProduct.camperSession?.upgradeCreditableDays || 0
  const newTotalDays = oldDays + cartProduct.days
  const volumeDiscountThreshold =
    cartProduct.product.volumeDiscountDays || Infinity
  const volumeDiscountNewlyTriggered =
    oldDays < volumeDiscountThreshold && newTotalDays >= volumeDiscountThreshold

  if (
    cartProduct.plan &&
    cartProduct.product.id === cartProduct.plan.product.id &&
    !hasBalanceDue &&
    !volumeDiscountNewlyTriggered
  ) {
    // Simple addition: same product, no balance due, volume discount not newly triggered
    // Use this even if day price changed due to FA opening (backend handles retroactive FA refund)
    return cartProduct.days * dayPriceCents
  }

  // Product upgrade, volume discount newly triggered, or receivable balance: recalculate entire plan value with credits
  const creditedAmountCents = creditableAmount(cartProduct)
  const creditedDays = creditableDays(cartProduct)
  const newPlanCostCents = (creditedDays + cartProduct.days) * dayPriceCents

  return newPlanCostCents - creditedAmountCents
}

// Return the amount of credit towards upgrade for value of days already purchased
const creditableAmount = cartProduct => {
  return cartProduct.plan?.financials?.payment || 0
}

const creditableDays = cartProduct => {
  return cartProduct.plan?.days?.payment || 0
}

// Total "account discount" reduction off the Summer Pass price
const summerPassDiscountAmount = cartProduct => {
  if (!hasProduct(cartProduct)) return 0

  const { product, accountDiscount } = cartProduct
  const { breakEvenDays = 0 } = product || {}
  const { discountCents = 0 } = accountDiscount || {}

  return discountCents * breakEvenDays
}

// Account discount category for the Summer Pass
const summerPassDiscountCategory = cartProduct => {
  const { accountDiscount } = cartProduct
  const { discountCategory } = accountDiscount || {}

  return discountCategory
}

// Full retail price for a Summer Pass
const summerPassRetailPrice = cartProduct => {
  if (!hasProduct(cartProduct)) return 0

  const { product } = cartProduct
  const {
    variablePriceCents = 0,
    breakEvenDays = 0,
    volumeDiscountCents = 0
  } = product

  return (variablePriceCents - volumeDiscountCents) * breakEvenDays
}

// return the calculated price for a single day on this plan,
// even if it is higher than the current day price
const newDayPrice = (cartProduct, days, account = null) => {
  if (!cartProduct || !cartProduct.product) {
    return 0
  }

  // account discount per day
  const accountDiscountCents = getAccountDiscountCents(cartProduct, account)

  // volume discount per day
  const { upgradeCreditableDays } = cartProduct.camperSession
  const volumeDiscountDays = upgradeCreditableDays + days
  const volumeDiscountCents =
    volumeDiscountDays >= cartProduct.product.volumeDiscountDays
      ? cartProduct.product.volumeDiscountCents
      : 0

  // new day price
  return (
    cartProduct.product.variablePriceCents -
    volumeDiscountCents -
    accountDiscountCents
  )
}

// Day price for this plan
const dayPrice = (cartProduct, days, account = null) => {
  const newDayPriceCents = newDayPrice(cartProduct, days, account)

  // don't allow day price to increase if discount is locked in
  const currentDayPrice = cartProduct.plan?.dayPriceCents
  if (currentDayPrice) {
    return Math.min(newDayPriceCents, currentDayPrice)
  } else {
    return newDayPriceCents
  }
}

const summerPassUpgradeDays = cartProduct => {
  // get number of days for upgrade
  const breakEvenDays = cartProduct.product?.breakEvenDays || 0
  const upgradeCreditableDays =
    cartProduct.camperSession.upgradeCreditableDays || 0

  return Math.max(breakEvenDays - upgradeCreditableDays, 0)
}

// Summer Pass upgrade price for this plan
const summerPassUpgradePrice = (cartProduct, account = null) => {
  const days = summerPassUpgradeDays(cartProduct)

  // get new plan cost
  const dayPriceCents = dayPrice(cartProduct, days, account)
  const creditedAmountCents = creditableAmount(cartProduct)
  const creditedDays = creditableDays(cartProduct)
  const newPlanCostCents = (creditedDays + days) * dayPriceCents

  // final amount owed
  return newPlanCostCents - creditedAmountCents
}

const productDescription = ({ product, session, days, selectedType }) => {
  if (selectedType == 'unlimited') {
    return product.unlimitedDescription
  }

  if (selectedType == 'volume-discount') {
    return `${days} ${session.name} ${product.volumeDiscountDescription}`
  }

  return `${days} ${session.name} ${product.description}`
}

// Minimum cost to upgrade to volume discount
const volumeDiscountUpgradeAmount = (cartProduct, account = null) => {
  if (!hasProduct(cartProduct)) return 0

  // get total number of retail days (only on this plan) to qualify for volume discount
  const days =
    volumeDiscountUpgradeDays(cartProduct) + creditableDays(cartProduct)

  // get volume discount price per day
  const retailPricePerDay = cartProduct.product.variablePriceCents || 0
  const accountDiscountCentsPerDay = getAccountDiscountCents(
    cartProduct,
    account
  )
  const volumeDiscountCentsPerDay = cartProduct.product.volumeDiscountCents || 0
  const dayPrice =
    retailPricePerDay - volumeDiscountCentsPerDay - accountDiscountCentsPerDay

  // get credit for purchased days
  const credit = creditableAmount(cartProduct)

  // return total cost to upgrade
  return days * dayPrice - credit
}

// Minimum number of days required to qualify for volume discount
const volumeDiscountUpgradeDays = cartProduct => {
  if (!hasProduct(cartProduct)) return 0

  if (volumeDiscountApplied(cartProduct)) {
    return 0
  }

  const { product, camperSession } = cartProduct
  const { upgradeCreditableDays } = camperSession

  return Math.max(product.volumeDiscountDays - upgradeCreditableDays, 0)
}

const volumeDiscountDayPrice = (cartProduct, account = null) => {
  if (!hasProduct(cartProduct)) return 0

  const variablePriceCents = cartProduct.product.variablePriceCents || 0
  const accountDiscountCents = getAccountDiscountCents(cartProduct, account)
  const volumeDiscountCents = cartProduct.product.volumeDiscountCents || 0

  return variablePriceCents - accountDiscountCents - volumeDiscountCents
}

// volume discount has been applied if volume discount is greater than or equal to day price
const volumeDiscountApplied = cartProduct => {
  if (!hasProduct(cartProduct)) return 0
  if (!cartProduct.product.volumeDiscountCents) return 0

  return volumeDiscountDayPrice(cartProduct) >= cartProduct.plan?.dayPriceCents
}

export {
  hasDays,
  cartProductTotal,
  productDescription,
  // summer pass discounts:
  summerPassUpgradeDays,
  summerPassUpgradePrice,
  summerPassDiscountAmount,
  summerPassDiscountCategory,
  summerPassRetailPrice,
  // volume discounts:
  volumeDiscountUpgradeDays,
  volumeDiscountUpgradeAmount,
  volumeDiscountDayPrice,
  volumeDiscountApplied
}
